import { useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client';
import dynamic from 'next/dynamic';

import {
  AppLayout,
  useUser,
  UserContextProvider,
  NotificationContextProvider,
} from '@portal/app';
import { ThemeProvider } from '@portal/ui';
import {
  useAlert,
  useAuth,
  useClient,
  useLocale,
  usePreviousPath,
  useToken,
} from '@portal/hooks';
import {
  atsFireEvent,
  atsInit,
  configureYup,
  Event,
  PublicRoutes,
  Route,
  RoutesWithoutAuth,
  RoutesWithoutNavBar,
  RoutesWithSideBar,
} from '@portal/lib';

const NotificationPoll = dynamic(
  () => import('@portal/notifications').then((mod) => mod.NotificationPoll),
  { ssr: false }
);
const Alert = dynamic(() => import('@portal/ui').then((mod) => mod.Alert), {
  ssr: false,
});
const Toaster = dynamic(
  () => import('react-hot-toast').then((mod) => mod.Toaster),
  { ssr: false }
);

import '@portal/styles/index.scss';

const App = ({ Component, pageProps }) => {
  configureYup();
  usePreviousPath();

  const router = useRouter();
  const { locale } = useLocale();
  const { alert, clearAlert } = useAlert();

  useEffect(() => {
    atsInit();
  }, []);

  // CloudFront serves root index.html for all routes as an SPA fallback.
  // After hydration, router.pathname reflects __NEXT_DATA__.page ("/") while
  // router.asPath reflects the real browser URL ("/auth/login", etc).
  // Navigate to the real URL so the correct page component renders.
  //
  // Until that correction lands, router.pathname/route is wrong ("/"), so we
  // must not mount Component or compute auth state from it — otherwise the
  // wrong page's effects (e.g. Overview's redirect to /leads) can race the
  // replace() below and win, and isAuthenticatedRoute can be momentarily
  // miscalculated for a public route.
  // Only check for the "/" fallback specifically — router.pathname is the
  // literal file-based route template (e.g. "/leads/[id]"), which never
  // equals the resolved asPath for any dynamic route, so comparing them
  // directly would permanently "mismatch" every dynamic page.
  const actualPath = router.asPath.split('?')[0].replace(/(.)\/$/, '$1');
  const isPathMismatched = router.pathname === '/' && actualPath !== '/';

  useEffect(() => {
    if (isPathMismatched) {
      router.replace(router.asPath);
    }
  }, []);

  const { isAuthenticatedRoute, isPublicRoute } = useMemo(() => {
    if (isPathMismatched) {
      return { isAuthenticatedRoute: false, isPublicRoute: false };
    }

    const isPublicRoute = PublicRoutes.includes(router.route);
    const isNoAuthRoute = RoutesWithoutAuth.includes(router.route);
    return {
      isAuthenticatedRoute: !isNoAuthRoute,
      isPublicRoute,
    };
  }, [router.route, isPathMismatched]);

  // Retrieve token
  const { token, userId, clearToken, saveToken } = useToken();

  // Validate token
  const { redirectToLogin, redirectToSavedPath } = useAuth({
    isAuthenticatedRoute,
    saveToken,
    token,
  });

  // Initialize graphql client
  const client = useClient({ token, isPublicRoute });

  // Retrieve user
  const user = useUser({
    client,
    isPublicRoute,
    onError: redirectToLogin,
    onSuccess: redirectToSavedPath,
    userId,
  });

  const storeId = user.data?.meta.currentStore?.id;

  useEffect(() => {
    if (router?.pathname && router?.pathname !== '/' && storeId) {
      atsFireEvent(Event.ViewPage, { storeId });
    }
  }, [router?.asPath, router?.pathname, storeId]);

  const navigationItems = [
    {
      text: locale.appNavLeads,
      path: Route.leadList.path,
      display: true,
    },
    {
      text: locale.appNavAppointments,
      path: Route.appointmentList.path,
      display: true,
    },
    {
      text: locale.appNavAppraisals,
      path: Route.appraisalList.path,
      display: user.data?.meta?.hasAppraisalStores,
    },
  ];

  const handleNavigationItemClick = ({ path }) => router.push(path);
  const handleLogoClick = () => router.push(Route.overview.path);

  const hasSideBar =
    router?.pathname && RoutesWithSideBar.includes(router?.pathname);
  const hasNavBarHidden =
    router?.pathname && RoutesWithoutNavBar.includes(router?.pathname);
  const isRedirectingToLogin = !token && isAuthenticatedRoute;
  const isAuthenticated = Boolean(token) && isAuthenticatedRoute;
  const isInitializing =
    isPathMismatched ||
    !client ||
    user.isLoading ||
    (isAuthenticatedRoute && !user.data);

  return (
    <ThemeProvider>
      <UserContextProvider value={user.data}>
        <NotificationContextProvider>
          <AppLayout
            activePath={router?.pathname || ''}
            hasNavBar={!hasNavBarHidden}
            hasSideBar={hasSideBar}
            isAuthenticated={isAuthenticated}
            isInitializing={isInitializing}
            isRedirectingToLogin={isRedirectingToLogin}
            navigationItems={navigationItems}
            onLogoClick={handleLogoClick}
            onLogoutClick={clearToken}
            onNavigationItemClick={handleNavigationItemClick}
          >
            <ApolloProvider client={client}>
              {isAuthenticated && <NotificationPoll />}
              {!isPathMismatched && (
                <Component {...pageProps} user={user.data} />
              )}
            </ApolloProvider>
            {alert.message && (
              <Alert
                type={alert.type}
                message={alert.message}
                onClose={clearAlert}
              />
            )}
            <Toaster position="bottom-right" reverseOrder />
          </AppLayout>
        </NotificationContextProvider>
      </UserContextProvider>
    </ThemeProvider>
  );
};

export default App;
